import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import React, { useState } from 'react';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import { getService } from '@Client/runner.hooks/useService';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import { SelectUser } from '@Shared.Angular/flowingly.components/SelectUser/SelectUser';
import { TagEditor } from '@Shared.Angular/flowingly.components/TagEditor/TagEditor';
import { InfoIcon } from '@Shared.Angular/flowingly.components/InfoIcon/InfoIcon';

type Props = {
  procedure: IProcedureDetail;
  onClose: () => void;
  onSave: (procedure: IProcedureDetail) => void;
};

const ProcedureDetailsModal = ({ procedure, onClose, onSave }: Props) => {
  const procedureApiService = getService('procedureApiService');
  const categoryApiService = getService('categoryApiService');
  const enableTagging = getService('APP_CONFIG').enableTagging;
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [categories, setCategories] = useState<ICategoryDetail[]>([]);
  const [categoryId, setCategoryId] = useState<Guid | null>(
    procedure.categoryId
  );
  const [ownerId, setOwnerId] = useState<Guid | null>(procedure.ownerId);
  const [background, setBackground] = useState<string>(
    procedure.background || ''
  );
  const [objective, setObjective] = useState<string>(procedure.objective || '');
  const [tagIds, setTagIds] = useState<Guid[]>(procedure.tagIds || []);

  React.useEffect(() => {
    categoryApiService
      .getCategories()
      .then((fetchedCategories) => {
        setCategories(fetchedCategories);
      })
      .catch(onClose);
  }, []);

  const saveChanges = () => {
    if (isRequestPending) return;
    setIsRequestPending(true);
    const editedProcedure = {
      ...procedure,
      categoryId,
      ownerId,
      background,
      objective,
      tagIds
    };
    procedureApiService
      .updateProcedure(editedProcedure)
      .then(() => {
        onSave(editedProcedure);
        setIsRequestPending(false);
      })
      .catch(() => {
        setIsRequestPending(false);
      });
  };

  const categorySelected = (id: Guid) => {
    setCategoryId(id);
  };

  return (
    <Modal
      title="SOP Details"
      id="procedure-details-modal"
      actionButtonText="Save"
      onActionClick={saveChanges}
      onCancelClick={onClose}
      onClose={onClose}
      size="md"
    >
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <div className="flowingly-form">
        <div className="flowingly-field">
          <label htmlFor="procedure-owner">Owner</label>
          <SelectUser
            inputId="procedure-owner"
            selectedUserId={ownerId}
            onUserSelect={(userId) => setOwnerId(userId)}
            popupClassName="procedure-owner-dropdown-tree-popup modal-overlay"
          />
        </div>
        <div className="flowingly-field">
          <label>
            Category
            {procedure.published && (
              <InfoIcon message="Because this SOP is already published its category can only be changed when publishing" />
            )}
          </label>
          <KendoReactDropDownTree
            placeholder="Select Category..."
            categories={categories}
            className="modal-overlay"
            categoryClicked={categorySelected}
            dataItemKey="id"
            textField="name"
            subItemsField="subCategories"
            selectedId={categoryId || EmptyGuid}
            disabled={procedure.published}
            showFilter={true}
          />
        </div>
        <div className="flowingly-field">
          <label>Objective</label>
          <input
            type="text"
            value={objective}
            id="procedure-objective"
            onChange={(e) => setObjective(e.target.value)}
          />
        </div>
        <div className="flowingly-field">
          <label>Background</label>
          <input
            type="text"
            value={background}
            id="procedure-background"
            onChange={(e) => setBackground(e.target.value)}
          />
        </div>
        {enableTagging && (
          <div className="flowingly-field">
            <TagEditor
              taggedEntityType="SOP"
              selectedTagIds={tagIds}
              editable={true}
              onSaved={(updatedTags) => setTagIds(updatedTags)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ProcedureDetailsModal;
